import { useMemo } from 'react';

/**
 * useChangeTracking Hook
 *
 * Tracks all changes compared to original values.
 * Used to determine if the user has unsaved changes and which
 * change types need to be displayed in the UI.
 *
 * @param {Object} params - Current and original state values
 * @returns {Object} Change flags and computed values
 */
export function useChangeTracking({
  // Caption
  caption = '',
  originalCaption = '',
  // Style
  style = {},
  originalStyle = {},
  // Position
  textPosition = { x: 0, y: 0 },
  originalPosition = { x: 0, y: 0 },
  // Alignment
  textAlignment = 'left',
  originalAlignment = 'left',
  // Background
  backgroundMode = 'solid',
  backgroundConfig = {},
  originalBackgroundConfig = {},
  // Font
  selectedFont = 'Classic',
  originalFont = 'Classic',
  // GIF
  gifMetadata = null,
  originalGifMetadata = null,
}) {
  /**
   * Check if caption text has changed
   */
  const hasCaptionChanges = useMemo(
    () => caption.trim() !== originalCaption.trim(),
    [caption, originalCaption]
  );

  /**
   * Check if style (colors, background toggle) has changed
   */
  const hasStyleChanges = useMemo(() => (
    style.textColor !== originalStyle.textColor ||
    style.backgroundColor !== originalStyle.backgroundColor ||
    style.textHasBackground !== originalStyle.textHasBackground
  ), [style, originalStyle]);

  /**
   * Check if text position has changed
   */
  const hasPositionChanges = useMemo(() => (
    textPosition.x !== originalPosition.x ||
    textPosition.y !== originalPosition.y
  ), [textPosition, originalPosition]);

  /**
   * Check if text alignment has changed
   */
  const hasAlignmentChanges = useMemo(
    () => textAlignment !== originalAlignment,
    [textAlignment, originalAlignment]
  );

  /**
   * Check if background configuration has changed
   */
  const hasBackgroundChanges = useMemo(() => {
    // Check if mode changed
    const modeChanged = backgroundMode !== originalBackgroundConfig.mode;

    // Check if solid color changed
    const solidColorChanged = backgroundMode === 'solid' &&
      backgroundConfig.backgroundColor !== originalBackgroundConfig.backgroundColor;

    // Check if gradient config changed
    const gradientChanged = backgroundMode === 'gradient' && (
      backgroundConfig.gradientType !== originalBackgroundConfig.gradientType ||
      backgroundConfig.gradientAngle !== originalBackgroundConfig.gradientAngle ||
      JSON.stringify(backgroundConfig.gradientColors) !== JSON.stringify(originalBackgroundConfig.gradientColors)
    );

    return modeChanged || solidColorChanged || gradientChanged;
  }, [backgroundMode, backgroundConfig, originalBackgroundConfig]);

  /**
   * Check if font has changed
   */
  const hasFontChanges = useMemo(
    () => selectedFont !== originalFont,
    [selectedFont, originalFont]
  );

  /**
   * Check if GIF sticker has changed
   */
  const hasGifChanges = useMemo(() => {
    // Simple check: if metadata exists where it didn't before (or vice versa)
    if ((gifMetadata === null) !== (originalGifMetadata === null)) {
      return true;
    }
    // If both exist, compare key properties
    if (gifMetadata && originalGifMetadata) {
      return (
        gifMetadata.id !== originalGifMetadata.id ||
        gifMetadata.position?.x !== originalGifMetadata.position?.x ||
        gifMetadata.position?.y !== originalGifMetadata.position?.y ||
        gifMetadata.scale !== originalGifMetadata.scale ||
        gifMetadata.opacity !== originalGifMetadata.opacity
      );
    }
    return false;
  }, [gifMetadata, originalGifMetadata]);

  /**
   * Check if any changes exist
   */
  const hasAnyChanges = useMemo(() => (
    hasCaptionChanges ||
    hasStyleChanges ||
    hasPositionChanges ||
    hasAlignmentChanges ||
    hasBackgroundChanges ||
    hasFontChanges ||
    hasGifChanges
  ), [
    hasCaptionChanges,
    hasStyleChanges,
    hasPositionChanges,
    hasAlignmentChanges,
    hasBackgroundChanges,
    hasFontChanges,
    hasGifChanges
  ]);

  /**
   * Get list of change types for UI display
   */
  const changeTypes = useMemo(() => {
    const types = [];
    if (hasStyleChanges) types.push('style');
    if (hasCaptionChanges) types.push('caption');
    if (hasPositionChanges) types.push('position');
    if (hasAlignmentChanges) types.push('alignment');
    if (hasBackgroundChanges) types.push(backgroundMode === 'gradient' ? 'gradient' : 'background');
    if (hasFontChanges) types.push('font');
    if (hasGifChanges) types.push('gif');
    return types;
  }, [
    hasStyleChanges,
    hasCaptionChanges,
    hasPositionChanges,
    hasAlignmentChanges,
    hasBackgroundChanges,
    hasFontChanges,
    hasGifChanges,
    backgroundMode
  ]);

  return {
    // Individual change flags
    hasCaptionChanges,
    hasStyleChanges,
    hasPositionChanges,
    hasAlignmentChanges,
    hasBackgroundChanges,
    hasFontChanges,
    hasGifChanges,
    // Computed
    hasAnyChanges,
    changeTypes,
  };
}

export default useChangeTracking;
